import Rating from "@JohnLewisPartnership/jl-design-system/dist/components/rating/Rating";
import styles from "./RatingAndReviews.module.scss";
import { PRODUCT_REVIEWS_ACCORDION_INSTANCE_NAME } from "../ProductAccordion/components/ProductReviewsAccordion/ProductReviewsAccordionUI";
import { scrollIntoViewAndWait } from "@/utils/scroll/scroll.ts";
import classNames from "classnames/bind";

const dispatchOpenProductReviewsEvent = () => {
  document.body.dispatchEvent(
    new CustomEvent(
      `jlds:accordion:${PRODUCT_REVIEWS_ACCORDION_INSTANCE_NAME}`,
      {
        detail: { action: "open" },
      },
    ),
  );
};

type Props = {
  /**
   * An object representing the rating of the product. It can also be null or undefined. The rating object has 'averageRating' and 'reviews' properties.
   */
  rating:
    | {
        averageRating: number;
        reviews: number;
      }
    | null
    | undefined;
  /**
   * Hide ratings
   */
  hideRatings?: boolean;
  /**
   * Suppress ratings link
   */
  suppressRatingLink?: boolean;
  /**
   * Has the link been moved to render on its own?
   */
  moveReviewsSkipLink?: boolean;
};

export const RatingAndReviewsUI = ({
  rating,
  hideRatings = false,
  suppressRatingLink = false,
  moveReviewsSkipLink = false,
}: Props) => {
  const cx = classNames.bind(styles);
  const handleReviewClick = (e: React.SyntheticEvent) => {
    e.preventDefault();
    dispatchOpenProductReviewsEvent();

    const scrollIntoView = () => {
      const reviewSection = document.querySelector("#ratings-reviews-section");

      if (!reviewSection) return;

      scrollIntoViewAndWait(reviewSection, {
        behavior: "smooth",
        block: "start",
      })
        .then(() => {
          const rect = reviewSection.getBoundingClientRect();

          if (rect.top !== 0) {
            reviewSection.scrollIntoView({
              behavior: "instant",
              block: "start",
            });
          }
        })
        .then(() => {
          if (reviewSection instanceof HTMLDetailsElement) {
            // Reviews are in the accordion, so we can focus the summary
            const reviewSummary = reviewSection.querySelector("summary");
            if (reviewSummary) {
              reviewSummary.focus();
            }
          } else {
            // Reviews are in the page body, so we need to focus the jl-reviews ESI's heading
            const reviewsESIHeading = reviewSection
              .querySelector("jl-reviews")
              ?.shadowRoot?.querySelector("h2");
            if (reviewsESIHeading) {
              reviewsESIHeading.setAttribute("tabindex", "-1");
              reviewsESIHeading.focus();
            }
          }
        });
    };

    setTimeout(scrollIntoView, 0);
  };

  if (!rating || rating.reviews === 0 || hideRatings) {
    return null;
  }

  const ratingText = `(${rating.reviews})`;
  const ratingsClass = moveReviewsSkipLink
    ? `${styles.ratings} ${styles.ratingsUngrouped}`
    : styles.ratings;

  return (
    <>
      {suppressRatingLink ? (
        <div
          className={ratingsClass}
          title={`${rating.averageRating} out of 5 stars`}
          aria-label={`${rating.averageRating} out of 5 stars, ${ratingText}`}
          data-testid="rating-reviews-wrapper"
        >
          <Rating size="large" suffixType="none" rating={rating} />
          <span className={styles.ratingText}>{ratingText}</span>
        </div>
      ) : (
        <a
          className={ratingsClass}
          title={`${rating.averageRating} out of 5 stars`}
          aria-label={`${rating.averageRating} out of 5 stars, ${ratingText}`}
          onClick={handleReviewClick}
          href="#ratings-reviews-section"
          data-testid="rating-reviews-section"
        >
          <Rating size="large" suffixType="none" rating={rating} />
          <span className={cx(styles.ratingText, styles.link)}>
            {ratingText}
          </span>
        </a>
      )}
    </>
  );
};
